import type { Timestamp } from "firebase/firestore";
import { action, computed, makeObservable } from "mobx";
import type { TKey } from "../../shared/translations/translationKeys";
import type { Api } from "../core/apis";
import { BaseModel, type BaseModelData } from "../core/BaseModel";
import type { Stores } from "../core/stores";
import type { IconType } from "../core/types";
import type { InfoRowItemProps } from "../ui/InfoRow";
import { type ActivityEventType, REACTIONS, type Reaction } from "./types";

export type UserReaction = {
  userUid: string;
  reaction: Reaction;
};

export interface ActivityModelData extends BaseModelData {
  planUid: string;
  moduleUid: string;
  userUid: string;
  dataUid: string | null;

  type: ActivityEventType;
  timestamp: Timestamp;
  icon?: IconType;

  textSID: TKey;
  textData: Record<string, string | number>;

  text?: string; // deprecated

  reactions: UserReaction[];
}

export default class ActivityModel extends BaseModel<ActivityModelData> {
  constructor(stores: Stores, api: Api, data: ActivityModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      textSID: data.textSID ?? "",
      textData: data.textData ?? {},
    });

    makeObservable(this);
  }

  @action async deleteMe() {
    await this.module.deleteRecentActivityEventToModule(this);
    await this.api.activitiesApi.delete(this);
  }

  @action async toggleReaction(reaction: Reaction) {
    const currentUserUid = this.plan.userInPlan?.data.uid;
    if (!this.data.userUid || !currentUserUid) {
      return undefined;
    }

    const hasReacted = this.data.reactions.findIndex(
      (r) => r.reaction === reaction && r.userUid === currentUserUid,
    );
    if (hasReacted !== -1) {
      this.data.reactions.splice(hasReacted, 1);
    } else {
      this.data.reactions.push({
        userUid: currentUserUid,
        reaction,
      });
    }

    await this.api.activitiesApi.update(this.data.uid, {
      reactions: this.data.reactions,
    });

    // Also update the same in the module if found
    const moduleActivitySnapshot = this.module.data.recentActivity.find(
      (a) => a.uid === this.uid,
    );
    if (moduleActivitySnapshot) {
      moduleActivitySnapshot.reactions = this.data.reactions;
      await this.api.modulesApi.update(this.data.moduleUid, {
        recentActivity: this.module.data.recentActivity,
      });
    }
  }

  @computed get plan() {
    const p = this.stores.plansStore.byId.get(this.data.planUid);
    if (!p) {
      throw Error("Plan not found");
    }
    return p;
  }

  @computed get module() {
    const p = this.stores.modulesStore.byId.get(this.data.moduleUid);
    if (!p) {
      throw Error("Module not found");
    }
    return p;
  }

  @computed get reactionCounts(): InfoRowItemProps[] {
    const anyReaction: InfoRowItemProps[] = [];
    REACTIONS.forEach((r) => {
      const c = this.data.reactions.filter(
        (rr) => rr.reaction === r.type,
      ).length;
      if (c) {
        anyReaction.push({
          icon: r.icon,
          text: `${c}`,
        });
      }
    });
    return anyReaction;
  }

  @computed get user() {
    if (!this.data.userUid) {
      return undefined;
    }

    const u = this.stores.usersStore.byId.get(this.data.userUid);
    if (!u) {
      return undefined;
    }
    return u;
  }

  toFireStore(): Omit<ActivityModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
